import React, { useCallback, useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import logo from "@/assets/logo.png";
import {
    Bell,
    Boxes,
    ChefHat,
    ChevronLeft,
    ChevronRight,
    Layers,
    LayoutDashboard,
    MonitorSmartphone,
    Package,
    PackagePlus,
    Percent,
    Plus,
    Settings,
    ShieldCheck,
    Star,
    Tag,
    Ticket,
    Truck,
    Users,
    Wallet,
    MapPin,
    RotateCcw,
    BarChart2,
    FileText,
} from "lucide-react";
import { IconTooltip } from "../common/IconTooltip";
import { useActiveRole } from "@/hooks/useActiveRole";
import { RoutesLink } from "@/utility/RouteLinks";
import { RootState, useAppDispatch, useAppSelector } from "@/redux/store";
import WithPermission from "@/components/common/WithPermission";
import { RESOURCES } from "@/context/permissions";
import { usePermissions } from "@/hooks/usePermissions";
import { saveSideBarScrollPosition, setCollapsed } from "@/redux/features/UserSlice";

const RESOURCE_MAP: Record<string, typeof RESOURCES[keyof typeof RESOURCES]> = {
    'Stores': RESOURCES.STORE,
    'Category': RESOURCES.CATEGORY,
    'Varients': RESOURCES.VARIANT,
    'Add-Ons': RESOURCES.ADDON,
    'Products': RESOURCES.PRODUCT,
    'Orders': RESOURCES.ORDER,
    'Return Orders': RESOURCES.RETURNS,
    'Return Reasons': RESOURCES.RETURN_REASON,
    'Discounts': RESOURCES.DISCOUNT,
    'Coupons': RESOURCES.COUPON,
    'Drivers': RESOURCES.DRIVER,
    'Zones': RESOURCES.ZONE,
    'Customers': RESOURCES.CUSTOMER,
    'Reviews': RESOURCES.REVIEW,
    'Home Layout': RESOURCES.HOME_LAYOUT,
    'Roles': RESOURCES.ROLE,
    'Staffs': RESOURCES.STAFF,
    'Live Orders': RESOURCES.LIVE_ORDER,
    'Tags': RESOURCES.TAG,
    'Cash Settlement': RESOURCES.CASH_SETTLEMENT,
    'Payouts': RESOURCES.PAYOUTS,
    'Store Settlements': RESOURCES.STORE_SETTLEMENT,
    'Analytics': RESOURCES.ANALYTICS,
};

interface NavItem {
    title: string;
    path: string;
    icon: React.ReactNode;
    relatedRoutes?: string[];
    allowedRoles?: (string | undefined)[];
}

interface SidebarProps {
    mobileOpen: boolean;
    setMobileOpen: (value: boolean) => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ mobileOpen, setMobileOpen }) => {
    const location = useLocation();
    const { prefix } = useActiveRole();
    const { isAdmin } = usePermissions();
    const user = useAppSelector((state: RootState) => state?.user?.user);
    const { collapsed, scrollPosition } = useAppSelector((state: RootState) => state?.user);
    const roleName = user?.role?.name;
    const dispatch = useAppDispatch()

    useEffect(() => {
        const sidebarScroll = document.getElementById("sideWrapper");
        const handleScroll = () => {
            const scrollTop = sidebarScroll.scrollTop;
            dispatch(saveSideBarScrollPosition(scrollTop));
        };
        sidebarScroll?.addEventListener("scroll", handleScroll);

        return () => {
            sidebarScroll?.removeEventListener("scroll", handleScroll);
        };
    }, []);

    useEffect(() => {
        const sidebarScroll = document.getElementById("sideWrapper");
        if (sidebarScroll) {
            sidebarScroll.scrollTop = scrollPosition;
        }
    }, []);

    const navItems: NavItem[] = useMemo(() => [
        {
            title: "Dashboard",
            path: prefix + RoutesLink.dashboard,
            relatedRoutes: [prefix + RoutesLink.dashboard],
            icon: <LayoutDashboard className="w-4 h-4" />,
        },
        {
            title: "Analytics",
            path: prefix + RoutesLink.Analytics,
            relatedRoutes: [prefix + RoutesLink.Analytics],
            icon: <BarChart2 className="w-4 h-4" />,
        },
        ...(!isAdmin ? [{
            title: "Live Orders",
            path: prefix + RoutesLink.LiveOrders,
            relatedRoutes: [prefix + RoutesLink.LiveOrders],
            icon: <ChefHat className="w-4 h-4" />,
        }] : []),
        {
            title: "Stores",
            path: prefix + RoutesLink.stores,
            relatedRoutes: [
                prefix + RoutesLink.stores,
                prefix + RoutesLink.stores + "/:id",
            ],
            icon: <ChefHat className="w-4 h-4" />,
        },
        {
            title: "Category",
            path: prefix + RoutesLink.Category,
            relatedRoutes: [prefix + RoutesLink.Category],
            icon: <Layers className="w-4 h-4" />,
        },
        {
            title: "Varients",
            path: prefix + RoutesLink.Varients,
            relatedRoutes: [
                prefix + RoutesLink.Varients,
                prefix + RoutesLink.AddVarients,
                prefix + RoutesLink.EditVarients,
            ],
            icon: <PackagePlus className="w-4 h-4" />,
        },
        {
            title: "Add-Ons",
            path: prefix + RoutesLink.AddOns,
            relatedRoutes: [prefix + RoutesLink.AddOns, prefix + RoutesLink.AddAddOns, prefix + RoutesLink.EditAddOns],
            icon: <Plus className='w-4 h-4' />,
        },
        {
            title: "Products",
            path: prefix + RoutesLink.Products,
            relatedRoutes: [
                prefix + RoutesLink.Products,
                prefix + RoutesLink.AddProduct,
                prefix + RoutesLink.EditProduct,
                prefix + RoutesLink.ViewProduct,
            ],
            icon: <Package className="w-4 h-4" />,
        },
        {
            title: "Orders",
            path: prefix + RoutesLink.Orders,
            relatedRoutes: [
                prefix + RoutesLink.Orders,
                prefix + RoutesLink.ViewOrders,
            ],
            icon: <Boxes className="w-4 h-4" />,
        },
        {
            title: "Return Orders",
            path: prefix + RoutesLink.Return,
            relatedRoutes: [
                prefix + RoutesLink.Return,
                prefix + RoutesLink.ViewReturn,
            ],
            icon: <RotateCcw className="w-4 h-4" />,
        },
        {
            title: "Return Reasons",
            path: prefix + RoutesLink.ReturnReasons,
            relatedRoutes: [
                prefix + RoutesLink.ReturnReasons,
            ],
            icon: <FileText className="w-4 h-4" />,
        },
        {
            title: "Drivers",
            path: prefix + RoutesLink.Drivers,
            relatedRoutes: [
                prefix + RoutesLink.Drivers,
                prefix + RoutesLink.DriverDetails,
            ],
            icon: <Truck className="w-4 h-4" />,
        },
        {
            title: "Zones",
            path: prefix + RoutesLink.Zones,
            relatedRoutes: [
                prefix + RoutesLink.Zones,
                prefix + RoutesLink.AddZone,
                prefix + RoutesLink.EditZone,
            ],
            icon: <MapPin className="w-4 h-4" />,
        },
        {
            title: "Customers",
            path: prefix + RoutesLink.Customers,
            relatedRoutes: [
                prefix + RoutesLink.Customers,
                prefix + RoutesLink.CustomerDetails,
            ],
            icon: <Users className="w-4 h-4" />,
        },
        {
            title: "Roles",
            path: prefix + RoutesLink.Roles,
            relatedRoutes: [
                prefix + RoutesLink.Roles,
                prefix + RoutesLink.AddRole,
                prefix + RoutesLink.EditRole,
                prefix + RoutesLink.ViewRole,
            ],
            icon: <ShieldCheck className="w-4 h-4" />,
        },
        {
            title: "Notifications",
            path: prefix + RoutesLink.Notifications,
            relatedRoutes: [prefix + RoutesLink.Notifications],
            icon: <Bell className="w-4 h-4" />,
        },
        {
            title: "Reviews",
            path: prefix + RoutesLink.Reviews,
            relatedRoutes: [prefix + RoutesLink.Reviews],
            icon: <Star className="w-4 h-4" />,
        },
        {
            title: "Home Layout",
            path: prefix + RoutesLink.HomeLayout,
            relatedRoutes: [prefix + RoutesLink.HomeLayout],
            icon: <MonitorSmartphone className="w-4 h-4" />,
        },
        {
            title: "Tags",
            path: prefix + RoutesLink.Tags,
            relatedRoutes: [prefix + RoutesLink.Tags],
            icon: <Tag className="w-4 h-4" />,
        },
        {
            title: "Cash Settlement",
            path: prefix + RoutesLink.CashSettlement,
            relatedRoutes: [prefix + RoutesLink.CashSettlement],
            icon: <Wallet className="w-4 h-4" />,
        },
        {
            title: "Store Settlements",
            path: prefix + RoutesLink.StoreSettlements,
            relatedRoutes: [prefix + RoutesLink.StoreSettlements],
            icon: <Wallet className="w-4 h-4 " />
        },
        {
            title: "Payouts",
            path: prefix + RoutesLink.payouts,
            relatedRoutes: [prefix + RoutesLink.payouts],
            icon: <Wallet className="w-4 h-4 " />,
        },
        ...(!isAdmin ? [{
            title: "Discounts",
            path: prefix + RoutesLink.Discount,
            relatedRoutes: [prefix + RoutesLink.Discount, prefix + RoutesLink.AddDiscount, prefix + RoutesLink.EditDiscount, prefix + RoutesLink.ViewDiscount],
            icon: <Percent className='w-4 h-4' />,
        }] : []),
        {
            title: "Coupons",
            path: prefix + RoutesLink.Coupons,
            relatedRoutes: [prefix + RoutesLink.Coupons, prefix + RoutesLink.AddCoupon, prefix + RoutesLink.EditCoupon, prefix + RoutesLink.ViewCoupon],
            icon: <Ticket className='w-4 h-4' />,
        },
        {
            title: "Settings",
            path: prefix + RoutesLink.Settings,
            relatedRoutes: [prefix + RoutesLink.Settings],
            icon: <Settings className="w-4 h-4" />,
        },
    ], [prefix, isAdmin]);


    const isRouteActive = useCallback((relatedRoutes: string[] = []) => {
        return relatedRoutes.some((route) => {
            if (route.includes("/:")) {
                const baseRoute = route.split("/:")[0];
                return location.pathname.startsWith(baseRoute);
            }
            return location.pathname === route;
        });
    }, [location.pathname]);

    return (
        <aside
            className={`fixed left-0 top-0 bottom-0 z-20 flex flex-col ${collapsed ? "w-20" : "w-64"} flex-shrink-0 bg-sidebar text-sidebar-foreground transition-all duration-300 border-r border-sidebar-border ${mobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
                }`}
        >
            {/* Logo */}
            <div className="h-14 flex items-center gap-2.5 px-4 border-b border-sidebar-border" >
                <img
                    src={logo}
                    alt="Yebo Eggs"
                    className="w-8 h-8 rounded-full flex-shrink-0"
                />
                {!collapsed && (
                    <div className="overflow-hidden">
                        <p className="text-sm font-semibold text-sidebar-accent-foreground truncate">
                            Yebo Eggs
                        </p>
                        <p className="text-[10px] text-sidebar-muted truncate">
                            {roleName}
                        </p>
                    </div>
                )}
            </div>

            {/* Nav items */}
            <nav id="sideWrapper" className="flex-1 py-4 px-2 space-y-1 overflow-y-auto overflow-x-hidden scrollbar-thin transition-all">
                {navItems.map((item, index) => {
                    const active = isRouteActive(item.relatedRoutes);
                    // Dashboard & Notifications don't need fine-grained permission checks
                    const noPermissionCheck = ['Dashboard', 'Notifications', 'Settings'].includes(item.title);

                    const linkEl = (
                        <IconTooltip content={collapsed ? item.title : ""} side="right" key={index}>
                            <Link
                                to={item.path}
                                onClick={() => {
                                    dispatch(saveSideBarScrollPosition(scrollPosition));
                                    setMobileOpen(false);
                                }}
                                className={`flex items-center gap-2.5 h-9 px-2.5 rounded-md text-sm transition-colors ${active
                                    ? "bg-sidebar-primary text-sidebar-primary-foreground font-medium"
                                    : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                                    } ${collapsed ? "justify-center" : ""}`}
                            >
                                <span className="w-4 h-4 flex-shrink-0">{item.icon}</span>
                                {!collapsed && <span className="truncate">{item.title}</span>}
                            </Link>
                        </IconTooltip>
                    );

                    const resource = RESOURCE_MAP[item.title];

                    if (noPermissionCheck || !resource) {
                        return linkEl;
                    }

                    return (
                        <WithPermission key={index} resource={resource} action="view">
                            {linkEl}
                        </WithPermission>
                    );
                })}
            </nav>

            {/* Collapse toggle */}
            <div className="p-2 border-t border-sidebar-border hidden md:block">
                <IconTooltip content={collapsed ? "Expand Sidebar" : "Collapse Sidebar"} side="right" className="w-full">
                    <button
                        type="button"
                        className="w-full flex items-center justify-center h-8 rounded-md text-sidebar-muted hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors"
                        onClick={() => dispatch(setCollapsed(!collapsed))}
                    >
                        {collapsed ? (
                            <ChevronRight className="w-4 h-4" />
                        ) : (
                            <ChevronLeft className="w-4 h-4" />
                        )}
                    </button>
                </IconTooltip>
            </div>
        </aside>
    );
};
